import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { finalize } from "rxjs/operators";
import {
    ContactService,
    ContactTopicValue,
} from "src/app/shared/services/contact.service";

interface ContactChannel {
    label: string;
    value: string;
    href: string;
    icon: string;
    external?: boolean;
}

interface ContactTopic {
    value: ContactTopicValue;
    label: string;
}

@Component({
    selector: "app-contact-page",
    templateUrl: "./contact-page.component.html",
    styleUrls: ["./contact-page.component.scss"],
})
export class ContactPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("pageRoot") pageRoot?: ElementRef<HTMLElement>;

    pageEntered = false;
    formSubmitted = false;
    submitting = false;
    submitted = false;
    submitError: string | null = null;

    private revealObserver?: IntersectionObserver;

    readonly contactForm = new FormGroup({
        name: new FormControl<string | null>(null, [Validators.required]),
        email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
        phone: new FormControl<string | null>(null),
        topic: new FormControl<string>("product", [Validators.required]),
        message: new FormControl<string | null>(null, [
            Validators.required,
            Validators.minLength(20),
        ]),
    });

    readonly intlTelOptions: Record<string, unknown> = {
        initialCountry: "mg",
        separateDialCode: true,
        loadUtils: () => import("intl-tel-input/build/js/utils.js" as any),
    };

    readonly topics: ContactTopic[] = [
        { value: "product", label: "Question sur le produit" },
        { value: "enterprise", label: "Formule Entreprise ou volume" },
        { value: "support", label: "Support technique" },
        { value: "partnership", label: "Partenariat ou presse" },
        { value: "other", label: "Autre demande" },
    ];

    readonly channels: ContactChannel[] = [
        {
            label: "Email",
            value: "contact@rayalina.com",
            href: "mailto:contact@rayalina.com",
            icon: "bx-envelope",
        },
        {
            label: "Facebook",
            value: "Rayalina",
            href: "https://www.facebook.com/rayalinapp",
            icon: "bxl-facebook",
            external: true,
        },
        {
            label: "Instagram",
            value: "@rayalinapp",
            href: "https://www.instagram.com/rayalinapp",
            icon: "bxl-instagram",
            external: true,
        },
    ];

    readonly responseHighlights = [
        { icon: "bx-time-five", text: "Réponse sous 2 jours ouvrés en moyenne" },
        { icon: "bx-message-detail", text: "En français, adapté au contexte malgache" },
        { icon: "bx-store", text: "Équipe habituée aux boutiques et vendeurs sociaux" },
    ];

    constructor(private readonly contactService: ContactService) {}

    ngOnInit(): void {
        requestAnimationFrame(() => {
            this.pageEntered = true;
        });
    }

    ngAfterViewInit(): void {
        if (typeof IntersectionObserver === "undefined") {
            this.markAllRevealsVisible();
            return;
        }

        this.revealObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }
                    entry.target.classList.add("is-visible");
                    this.revealObserver?.unobserve(entry.target);
                }
            },
            { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
        );

        this.observeReveals();
    }

    ngOnDestroy(): void {
        this.revealObserver?.disconnect();
    }

    onSubmit(): void {
        this.submitError = null;
        this.formSubmitted = true;
        this.submitted = false;

        if (!this.contactForm.valid) {
            return;
        }

        const { name, email, phone, topic, message } = this.contactForm.getRawValue();
        if (!name?.trim() || !email?.trim() || !message?.trim() || !topic) {
            this.submitError = "Impossible d'envoyer le message. Vérifiez les champs.";
            return;
        }

        this.submitting = true;

        this.contactService
            .submit({
                name: name.trim(),
                email: email.trim(),
                phone: phone?.trim() || undefined,
                topic: topic as ContactTopicValue,
                message: message.trim(),
            })
            .pipe(finalize(() => (this.submitting = false)))
            .subscribe({
                next: () => {
                    this.submitted = true;
                },
                error: (err: HttpErrorResponse) => {
                    this.submitError = this.resolveSubmitError(err);
                },
            });
    }

    resetForm(): void {
        this.contactForm.reset({
            name: null,
            email: null,
            phone: null,
            topic: "product",
            message: null,
        });
        this.formSubmitted = false;
        this.submitted = false;
        this.submitError = null;
    }

    topicLabel(value: string): string {
        return this.topics.find((t) => t.value === value)?.label ?? value;
    }

    private resolveSubmitError(err: HttpErrorResponse): string {
        if (err.status === 0) {
            return "Impossible de joindre le serveur. Réessayez dans un instant.";
        }
        const body = err.error;
        if (typeof body === "string" && body.trim()) {
            return body;
        }
        if (Array.isArray(body?.message)) {
            return "Vérifiez les champs du formulaire.";
        }
        if (typeof body?.message === "string" && body.message.trim()) {
            return body.message;
        }
        return "L'envoi a échoué. Réessayez ou écrivez-nous à contact@rayalina.com.";
    }

    private observeReveals(): void {
        const root = this.pageRoot?.nativeElement;
        if (!root || !this.revealObserver) {
            return;
        }

        root.querySelectorAll<HTMLElement>("[data-ray-reveal]:not(.is-visible)").forEach((el) => {
            this.revealObserver!.observe(el);
        });
    }

    private markAllRevealsVisible(): void {
        const root = this.pageRoot?.nativeElement;
        if (!root) {
            return;
        }
        root.querySelectorAll<HTMLElement>("[data-ray-reveal]").forEach((el) => {
            el.classList.add("is-visible");
        });
    }

    get name() {
        return this.contactForm.get("name");
    }
    get email() {
        return this.contactForm.get("email");
    }
    get phone() {
        return this.contactForm.get("phone");
    }
    get topic() {
        return this.contactForm.get("topic");
    }
    get message() {
        return this.contactForm.get("message");
    }
}
